import { TimeFormatIdentifiers } from "../../../../constants/timeFormats";
import { createFromJS } from "../../../../types";
import calendarTypes from "../../../../types/calendarTypes";
import floatToInt from "../../../../utils/floatToInt";
import { getMonthsInYearFromCalendar } from "../selectors";
import { calendarPrecalculations } from "./dateInternalCalculations";
import { timespanForComponents } from "./timespanFunctions";

const USE_AM_PM = true; // TODO: Pull this from somewhere else?

const FULL_DATE_FORMAT = "%a %d %b %G"; // without Era
// const FULL_TIME_FORMAT = USE_AM_PM ? "%I:%M:%S%p" : "%H:%M:%S";
// const HOUR_ONLY_TIME_FORMAT = USE_AM_PM ? "%I:00%p" : "%H:00";
// const HOUR_MINUTE_TIME_FORMAT = USE_AM_PM ? "%I:%M%p" : "%H:%M";

const getFullTimeFormat = useAmPm => (useAmPm ? "%I:%M:%S%p" : "%H:%M:%S");
const getHourOnlyTimeFormat = useAmPm => (useAmPm ? "%I:00%p" : "%H:00");
const getHourMinuteTimeFormat = useAmPm => (useAmPm ? "%I:%M%p" : "%H:%M");

const getUseAmPm = calendar => calendar.timeFormat === TimeFormatIdentifiers.Twelve;

/**
 * @param {number} minSecondsSpacing Less than 30
 */
const getValuesInSecondsScale = (minSecondsSpacing, useAmPm = USE_AM_PM) => {
  let majorSpan = null;
  let minorSpan = null;
  let majorFormat = null;
  let minorFormat = null;
  if (minSecondsSpacing < 5) {
    majorSpan = timespanForComponents(0, 0, 0, 0, 0, 0, 5);
    minorSpan = timespanForComponents(0, 0, 0, 0, 0, 0, 2.5);
  } else if (minSecondsSpacing < 10) {
    majorSpan = timespanForComponents(0, 0, 0, 0, 0, 0, 10);
    minorSpan = timespanForComponents(0, 0, 0, 0, 0, 0, 5);
  } else if (minSecondsSpacing < 15) {
    majorSpan = timespanForComponents(0, 0, 0, 0, 0, 0, 15);
    minorSpan = timespanForComponents(0, 0, 0, 0, 0, 0, 5);
  } else if (minSecondsSpacing < 20) {
    majorSpan = timespanForComponents(0, 0, 0, 0, 0, 0, 20);
    minorSpan = timespanForComponents(0, 0, 0, 0, 0, 0, 10);
  } else {
    majorSpan = timespanForComponents(0, 0, 0, 0, 0, 0, 30);
    minorSpan = timespanForComponents(0, 0, 0, 0, 0, 0, 10);
  }
  const fullTimeFormat = getFullTimeFormat(useAmPm);
  majorFormat = `${FULL_DATE_FORMAT} ${fullTimeFormat}`;
  minorFormat = fullTimeFormat;
  return createFromJS.DateTickIntervalRecord({
    majorSpan,
    majorFormat,
    minorSpan,
    minorFormat
  });
};

/**
 * @param {number} minSecondsSpacing Less than 1800
 */
const getValuesInMinutesScale = (minSecondsSpacing, useAmPm = USE_AM_PM) => {
  let majorSpan = null;
  let minorSpan = null;
  let majorFormat = null;
  let minorFormat = null;
  if (minSecondsSpacing < 60) {
    majorSpan = timespanForComponents(0, 0, 0, 0, 0, 1, 0);
    minorSpan = timespanForComponents(0, 0, 0, 0, 0, 0, 30);
  } else if (minSecondsSpacing < 2 * 60) {
    majorSpan = timespanForComponents(0, 0, 0, 0, 0, 2, 0);
    minorSpan = timespanForComponents(0, 0, 0, 0, 0, 1, 0);
  } else if (minSecondsSpacing < 5 * 60) {
    majorSpan = timespanForComponents(0, 0, 0, 0, 0, 5, 0);
    minorSpan = timespanForComponents(0, 0, 0, 0, 0, 0, 150);
  } else if (minSecondsSpacing < 10 * 60) {
    majorSpan = timespanForComponents(0, 0, 0, 0, 0, 10, 0);
    minorSpan = timespanForComponents(0, 0, 0, 0, 0, 5, 0);
  } else if (minSecondsSpacing < 15 * 60) {
    majorSpan = timespanForComponents(0, 0, 0, 0, 0, 15, 0);
    minorSpan = timespanForComponents(0, 0, 0, 0, 0, 5, 0);
  } else if (minSecondsSpacing < 20 * 60) {
    majorSpan = timespanForComponents(0, 0, 0, 0, 0, 20, 0);
    minorSpan = timespanForComponents(0, 0, 0, 0, 0, 10, 0);
  } else {
    majorSpan = timespanForComponents(0, 0, 0, 0, 0, 30, 0);
    minorSpan = timespanForComponents(0, 0, 0, 0, 0, 10, 0);
  }
  const fullTimeFormat = getFullTimeFormat(useAmPm);
  const hourMinuteTimeFormat = getHourMinuteTimeFormat(useAmPm);
  if (minSecondsSpacing < 2 * 60) {
    minorFormat = fullTimeFormat;
  } else {
    minorFormat = hourMinuteTimeFormat;
  }
  majorFormat = `${FULL_DATE_FORMAT} ${fullTimeFormat}`;
  return createFromJS.DateTickIntervalRecord({
    majorSpan,
    majorFormat,
    minorSpan,
    minorFormat
  });
};

/**
 * @param {number} minSecondsSpacing Less than seconds of half day
 * @param {number} hoursInDay
 */
const getValuesInHoursScale = (minSecondsSpacing, hoursInDay, useAmPm = USE_AM_PM) => {
  let majorSpan = null;
  let minorSpan = null;
  let majorFormat = null;
  let minorFormat = null;
  let matched = false;
  for (let hour = 1; hour < hoursInDay / 2; hour++) {
    if (hoursInDay % hour === 0 && minSecondsSpacing < hour * 3600) {
      majorSpan = timespanForComponents(0, 0, 0, 0, hour, 0, 0);
      if (hour === 1) {
        minorSpan = timespanForComponents(0, 0, 0, 0, 0, 30, 0);
      } else if (hour % 2 === 0) {
        minorSpan = timespanForComponents(0, 0, 0, 0, hour / 2, 0, 0);
      } else if (hour % 3 === 0) {
        minorSpan = timespanForComponents(0, 0, 0, 0, hour / 3, 0, 0);
      } else {
        minorSpan = timespanForComponents(0, 0, 0, 0, hour, 0, 0);
      }
      matched = true;
      break;
    }
  }
  const hourMinuteTimeFormat = getHourMinuteTimeFormat(useAmPm);
  majorFormat = `${FULL_DATE_FORMAT} ${hourMinuteTimeFormat}`;
  minorFormat = hourMinuteTimeFormat;
  if (matched) {
    return createFromJS.DateTickIntervalRecord({
      majorSpan,
      majorFormat,
      minorSpan,
      minorFormat
    });
  }
  return null;
};

/**
 * minSecondsSpacing less than seconds of a day
 * @param {number} hoursInDay
 */
const getValuesInDayScale = (hoursInDay, useAmPm = USE_AM_PM) => {
  let majorSpan = null;
  let minorSpan = null;
  let majorFormat = null;
  let minorFormat = null;

  let selectedSpacing = 1;

  majorSpan = timespanForComponents(0, 0, 0, selectedSpacing, 0, 0, 0);
  majorFormat = "%a %d %b %G %E";
  let hSpacing = hoursInDay;
  if (hoursInDay % 5 === 0) {
    hSpacing = hoursInDay / 5;
  } else if (hoursInDay % 4 === 0) {
    hSpacing = hoursInDay / 4;
  } else if (hoursInDay % 3 === 0) {
    hSpacing = hoursInDay / 3;
  } else if (hoursInDay % 2 === 0) {
    hSpacing = hoursInDay / 2;
  }
  minorSpan = timespanForComponents(0, 0, 0, 0, hSpacing, 0, 0);
  minorFormat = useAmPm ? "%I:00%p" : "%H:00";

  return createFromJS.DateTickIntervalRecord({
    majorSpan,
    majorFormat,
    minorSpan,
    minorFormat
  });
};

const getValuesInWeekScale = () => {
  // Week
  const majorSpan = timespanForComponents(0, 0, 1, 0, 0, 0, 0);
  const majorFormat = "%a %d %b %G %E";
  const minorFormat = "%a";
  const minorSpan = timespanForComponents(0, 0, 0, 1, 0, 0, 0);
  return createFromJS.DateTickIntervalRecord({
    majorSpan,
    majorFormat,
    minorSpan,
    minorFormat
  });
};

const absoluteDateTickIntervals = (calendar, unitScale, minPixelSpacing) => {
  const minSecondsSpacing = floatToInt(minPixelSpacing * unitScale);

  const precalculations = calendarPrecalculations(calendar);
  const monthCount = getMonthsInYearFromCalendar(calendar);

  let majorSpan = null;
  let majorFormat = null;
  let minorSpan = null;
  let minorFormat = null;

  const useAmPm = getUseAmPm(calendar);

  if (minSecondsSpacing < 30) {
    return getValuesInSecondsScale(minSecondsSpacing, useAmPm);
  }

  // Minutes
  if (minSecondsSpacing < 1800) {
    return getValuesInMinutesScale(minSecondsSpacing, useAmPm);
  }

  // Hours
  if (minSecondsSpacing < precalculations.secondsInDay / 2) {
    const tickIntervalRecord = getValuesInHoursScale(minSecondsSpacing, calendar.hoursInDay, useAmPm);
    if (tickIntervalRecord) {
      return tickIntervalRecord;
    }
  }

  // Days
  if (minSecondsSpacing <= precalculations.secondsInDay) {
    return getValuesInDayScale(calendar.hoursInDay, useAmPm);
  } else if (minSecondsSpacing < precalculations.secondsInWeek) {
    return getValuesInWeekScale();
  } else if (minSecondsSpacing < precalculations.avgSecondsInYear / 2) {
    // Months (if less than half a year)
    let minUnitSpacing = Math.ceil(minSecondsSpacing / precalculations.avgSecondsInMonth);

    let selectedSpacing = 1;
    while (selectedSpacing < minUnitSpacing) {
      // Not this one... increment until the next even divisor
      do {
        selectedSpacing++;
      } while (monthCount % selectedSpacing !== 0);
    }
    majorFormat = "%b %G %E";
    minorFormat = "%b";
    majorSpan = timespanForComponents(0, selectedSpacing, 0, 0, 0, 0, 0);
    minorSpan = timespanForComponents(0, 1, 0, 0, 0, 0, 0);
    return createFromJS.DateTickIntervalRecord({
      majorSpan,
      majorFormat,
      minorSpan,
      minorFormat
    });
  } else {
    // Years
    let minUnitSpacing = Math.ceil(minSecondsSpacing / precalculations.avgSecondsInYear);

    let selectedSpacing = 1;
    let selectedSubFact = 1;
    while (selectedSpacing < minUnitSpacing) {
      // Go from 1xxx -> 2xxx
      selectedSpacing *= 2;
      if (selectedSpacing >= minUnitSpacing) {
        selectedSubFact = 4;
        break;
      }
      // Go from 2xxx -> 5xxx
      selectedSpacing = (selectedSpacing * 5) / 2;
      selectedSubFact = 5;
      if (selectedSpacing >= minUnitSpacing) {
        break;
      }
      // Go from 5xxx -> 10xxx
      selectedSpacing *= 2;
    }
    majorFormat = "%G %E";
    majorSpan = timespanForComponents(selectedSpacing, 0, 0, 0, 0, 0, 0);

    if (selectedSpacing === 1) {
      if (monthCount % 4 === 0) {
        minorSpan = timespanForComponents(0, monthCount / 4, 0, 0, 0, 0, 0);
      } else if (monthCount % 3 === 0) {
        minorSpan = timespanForComponents(0, monthCount / 3, 0, 0, 0, 0, 0);
      } else if (monthCount % 2 === 0) {
        minorSpan = timespanForComponents(0, monthCount / 2, 0, 0, 0, 0, 0);
      } else {
        minorSpan = timespanForComponents(0, monthCount, 0, 0, 0, 0, 0);
      }
      minorFormat = "%b";
    } else if (selectedSpacing === 2) {
      minorSpan = timespanForComponents(1, 0, 0, 0, 0, 0, 0);
      minorFormat = "%g";
    } else {
      minorSpan = timespanForComponents(selectedSpacing / selectedSubFact, 0, 0, 0, 0, 0, 0);
      minorFormat = "%g";
    }
    return createFromJS.DateTickIntervalRecord({
      majorSpan,
      majorFormat,
      minorSpan,
      minorFormat
    });
  }
};

/**
 * Replace the target patterns with only one occurrence of the replace pattern
 * i.e. Once find a target pattern, will replace it with the pattern
 * then remove the other target patterns in the string.
 * @param {string} originalStr
 * @param {string[]} targetPatterns
 * @param {string} replacePattern
 */
const replacePattern = (originalStr, targetPatterns, replacePattern) => {
  let rp = replacePattern;
  let str = originalStr;
  targetPatterns.forEach(tp => {
    const replacedStr = str.replace(tp, rp);
    if (rp && replacedStr !== str) {
      rp = "";
    }
    str = replacedStr;
  });
  str = str.trim().replace(/\s\s+/g, " ");
  return str;
};

const dateTickIntervalsWeekType = (calendar, unitScale, minPixelSpacing, useAmPm = USE_AM_PM) => {
  const minSecondsSpacing = floatToInt(minPixelSpacing * unitScale);
  const precalculations = calendarPrecalculations(calendar);

  let majorSpan = null;
  let majorFormat = null;
  let minorSpan = null;
  let minorFormat = null;

  const weekTypeFormat = "%a %W";

  const fullTimeFormat = getFullTimeFormat(useAmPm);

  if (minSecondsSpacing < 30) {
    return getValuesInSecondsScale(minSecondsSpacing, useAmPm).set(
      "majorFormat",
      `${fullTimeFormat} ${weekTypeFormat}`
    );
  }

  const hourMinuteTimeFormat = getHourMinuteTimeFormat(useAmPm);

  // Minutes
  if (minSecondsSpacing < 1800) {
    return getValuesInMinutesScale(minSecondsSpacing, useAmPm).set(
      "majorFormat",
      `${hourMinuteTimeFormat} ${weekTypeFormat}`
    );
  }

  // Hours
  if (minSecondsSpacing < precalculations.secondsInDay / 2) {
    const intervalRecord = getValuesInHoursScale(minSecondsSpacing, calendar.hoursInDay, useAmPm);
    if (intervalRecord) {
      return intervalRecord.set("majorFormat", `${hourMinuteTimeFormat} ${weekTypeFormat}`);
    }
  }

  // Days
  if (minSecondsSpacing <= precalculations.secondsInDay) {
    return getValuesInDayScale(calendar.hoursInDay, useAmPm).set("majorFormat", `${weekTypeFormat}`);
  } else if (minSecondsSpacing < precalculations.secondsInWeek) {
    return getValuesInWeekScale().set("majorFormat", `${weekTypeFormat}`);
  } else {
    // Weeks
    let minUnitSpacing = Math.ceil(minSecondsSpacing / precalculations.secondsInWeek);

    let selectedSpacing = 1;
    let selectedSubFact = 1;
    while (selectedSpacing < minUnitSpacing) {
      selectedSpacing *= 2;
      if (selectedSpacing >= minUnitSpacing) {
        selectedSubFact = 4;
        break;
      }
      selectedSpacing = (selectedSpacing * 5) / 2;
      selectedSubFact = 5;
      if (selectedSpacing >= minUnitSpacing) {
        break;
      }
      selectedSpacing *= 2;
    }
    majorFormat = "%W";
    majorSpan = timespanForComponents(0, 0, selectedSpacing, 0, 0, 0, 0);

    if (selectedSpacing === 1) {
      minorSpan = timespanForComponents(0, 0, 1, 0, 0, 0, 0);
      minorFormat = "%a";
    } else if (selectedSpacing === 2) {
      minorSpan = timespanForComponents(0, 0, 1, 0, 0, 0, 0);
      minorFormat = "%w";
    } else {
      minorSpan = timespanForComponents(0, 0, selectedSpacing / selectedSubFact, 0, 0, 0, 0);
      minorFormat = "%w";
    }
    return createFromJS.DateTickIntervalRecord({
      majorSpan,
      majorFormat,
      minorSpan,
      minorFormat
    });
  }
};

const dateTickIntervalsDayType = (calendar, unitScale, minPixelSpacing, useAmPm = USE_AM_PM) => {
  const minSecondsSpacing = floatToInt(minPixelSpacing * unitScale);
  const precalculations = calendarPrecalculations(calendar);

  let majorSpan = null;
  let majorFormat = null;
  let minorSpan = null;
  let minorFormat = null;

  const dayTypeFormat = "%Z";

  const fullTimeFormat = getFullTimeFormat(useAmPm);

  if (minSecondsSpacing < 30) {
    return getValuesInSecondsScale(minSecondsSpacing, useAmPm).set("majorFormat", `${fullTimeFormat} ${dayTypeFormat}`);
  }

  const hourMinuteTimeFormat = getHourMinuteTimeFormat(useAmPm);

  // Minutes
  if (minSecondsSpacing < 1800) {
    return getValuesInMinutesScale(minSecondsSpacing, useAmPm).set(
      "majorFormat",
      `${hourMinuteTimeFormat} ${dayTypeFormat}`
    );
  }

  // Hours
  if (minSecondsSpacing < precalculations.secondsInDay / 2) {
    const intervalRecord = getValuesInHoursScale(minSecondsSpacing, calendar.hoursInDay, useAmPm);
    if (intervalRecord) {
      return intervalRecord.set("majorFormat", `${hourMinuteTimeFormat} ${dayTypeFormat}`);
    }
  }

  // Days
  if (minSecondsSpacing <= precalculations.secondsInDay) {
    // Less than a day
    return getValuesInDayScale(calendar.hoursInDay, useAmPm).set("majorFormat", `${dayTypeFormat}`);
  } else {
    // Days
    let minUnitSpacing = Math.ceil(minSecondsSpacing / precalculations.secondsInDay);

    let selectedSpacing = 1;
    let selectedSubFact = 1;
    while (selectedSpacing < minUnitSpacing) {
      selectedSpacing *= 2;
      if (selectedSpacing >= minUnitSpacing) {
        selectedSubFact = 4;
        break;
      }
      selectedSpacing = (selectedSpacing * 5) / 2;
      selectedSubFact = 5;
      if (selectedSpacing >= minUnitSpacing) {
        break;
      }
      selectedSpacing *= 2;
    }
    majorFormat = "%Z";
    majorSpan = timespanForComponents(0, 0, 0, selectedSpacing, 0, 0, 0);

    const hourOnlyTimeFormat = getHourOnlyTimeFormat(useAmPm);

    if (selectedSpacing === 1) {
      minorSpan = timespanForComponents(0, 0, 0, selectedSpacing, 0, 0, 0);
      minorFormat = `${hourOnlyTimeFormat}`;
    } else if (selectedSpacing === 2) {
      minorSpan = timespanForComponents(0, 0, 0, selectedSpacing, 0, 0, 0);
      minorFormat = "%z";
    } else {
      minorSpan = timespanForComponents(0, 0, 0, selectedSpacing / selectedSubFact, 0, 0, 0);
      minorFormat = "%z";
    }
    return createFromJS.DateTickIntervalRecord({
      majorSpan,
      majorFormat,
      minorSpan,
      minorFormat
    });
  }
};

const dateTickIntervalsHourType = (calendar, unitScale, minPixelSpacing) => {
  const minSecondsSpacing = floatToInt(minPixelSpacing * unitScale);

  let majorSpan = null;
  let majorFormat = null;
  let minorSpan = null;
  let minorFormat = null;

  const useAmPm = getUseAmPm(calendar);

  if (minSecondsSpacing < 30) {
    return getValuesInSecondsScale(minSecondsSpacing, useAmPm).set("majorFormat", "%X").set("minorFormat", "%X");
  }

  // Minutes
  if (minSecondsSpacing < 1800) {
    return getValuesInMinutesScale(minSecondsSpacing, useAmPm).set("majorFormat", "%X").set("minorFormat", "%X");
  }

  const secondsInHour = 3600;

  let minUnitSpacing = Math.ceil(minSecondsSpacing / secondsInHour);

  let selectedSpacing = 1;
  let selectedSubFact = 1;
  while (selectedSpacing < minUnitSpacing) {
    selectedSpacing *= 2;
    if (selectedSpacing >= minUnitSpacing) {
      selectedSubFact = 4;
      break;
    }
    selectedSpacing = (selectedSpacing * 5) / 2;
    selectedSubFact = 5;
    if (selectedSpacing >= minUnitSpacing) {
      break;
    }
    selectedSpacing *= 2;
  }
  majorFormat = "%X";
  majorSpan = timespanForComponents(0, 0, 0, 0, selectedSpacing, 0, 0);

  if (selectedSpacing === 1) {
    minorSpan = timespanForComponents(0, 0, 0, 0, 0, 15, 0);
    minorFormat = "%X";
  } else if (selectedSpacing === 2) {
    minorSpan = timespanForComponents(0, 0, 0, 0, 0, 30, 0);
    minorFormat = "%X";
  } else {
    minorSpan = timespanForComponents(0, 0, 0, 0, selectedSpacing / selectedSubFact, 0, 0);
    minorFormat = "%X";
  }
  return createFromJS.DateTickIntervalRecord({
    majorSpan,
    majorFormat,
    minorSpan,
    minorFormat
  });
};

const dateTickIntervalsByCalendarType = (calendar, unitScale, minPixelSpacing) => {
  const useAmPm = getUseAmPm(calendar);
  if (calendar.calendarType === calendarTypes.CALENDAR_TYPE_DATE_TIME) {
    return absoluteDateTickIntervals(calendar, unitScale, minPixelSpacing);
  } else if (calendar.calendarType === calendarTypes.CALENDAR_TYPE_WEEKDAY_TIME) {
    return dateTickIntervalsWeekType(calendar, unitScale, minPixelSpacing, useAmPm);
  } else if (calendar.calendarType === calendarTypes.CALENDAR_TYPE_DAY_TIME) {
    return dateTickIntervalsDayType(calendar, unitScale, minPixelSpacing, useAmPm);
  } else if (calendar.calendarType === calendarTypes.CALENDAR_TYPE_HOUR_TIME) {
    return dateTickIntervalsHourType(calendar, unitScale, minPixelSpacing);
  }
};

export default dateTickIntervalsByCalendarType;

export { replacePattern };
